import { RefreshCw01 } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';

import type { CreateMergeFieldBody } from '../api/fieldMappings';
import type { MailchimpFieldMapping } from '../types';
import {
  EditableMappingRow,
  MAPPING_ROW_GRID,
  type MappingUpdate,
} from './EditableMappingRow';

export interface AhoyPropertyOption {
  name: string;
  label: string;
  fieldType?: string;
}

interface MappingSectionsProps {
  mappings: MailchimpFieldMapping[];
  availableAhoyProperties: AhoyPropertyOption[];
  savingId: number | null;
  /** Ahoy property name currently being created as a Mailchimp merge field. */
  creatingProp: string | null;
  onUpdate: (mappingId: number, body: MappingUpdate) => void;
  onCreateMergeField: (body: CreateMergeFieldBody) => void;
}

const COLUMN_HEADER_LABEL_CLASS =
  'text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]';
const SECTION_HEADING_CLASS =
  'text-sm font-semibold text-[var(--color-text-primary)]';
const CAPTION_CLASS = 'text-body-small text-[var(--color-text-secondary)]';

function isSynced(m: MailchimpFieldMapping): boolean {
  return !!m.ahoy_property;
}

function MappingTable({
  mappings,
  availableAhoyProperties,
  savingId,
  onUpdate,
}: Pick<
  MappingSectionsProps,
  'mappings' | 'availableAhoyProperties' | 'savingId' | 'onUpdate'
>) {
  return (
    <div className='overflow-hidden rounded-xl border border-[var(--color-border-secondary)]'>
      <div
        className={`hidden bg-[var(--color-bg-secondary)] px-4 py-2.5 sm:block ${MAPPING_ROW_GRID}`}
      >
        <span className={COLUMN_HEADER_LABEL_CLASS}>Mailchimp field</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Ahoy property</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>Direction</span>
        <span className={COLUMN_HEADER_LABEL_CLASS}>On conflict</span>
      </div>
      <div className='divide-y divide-[var(--color-border-secondary)]'>
        {mappings.map(mapping => (
          <EditableMappingRow
            key={mapping.id}
            mapping={mapping}
            availableAhoyProperties={availableAhoyProperties}
            isSaving={savingId === mapping.id}
            onUpdate={onUpdate}
          />
        ))}
      </div>
    </div>
  );
}

interface AhoyFieldRowProps {
  option: AhoyPropertyOption;
  isCreating: boolean;
  onCreate: (body: CreateMergeFieldBody) => void;
}

function AhoyFieldRow({ option, isCreating, onCreate }: AhoyFieldRowProps) {
  const handleClick = useCallback(() => {
    onCreate({
      ahoy_property: option.name,
      name: option.label,
      field_type: option.fieldType ?? 'text',
    });
  }, [onCreate, option]);

  return (
    <div className='flex items-center justify-between gap-3 px-4 py-2.5'>
      <div className='min-w-0'>
        <span className='text-sm font-medium text-[var(--color-text-primary)]'>
          {option.label}
        </span>
        <span className='ml-2 text-xs text-[var(--color-text-tertiary)]'>
          {option.name}
        </span>
      </div>
      <Button
        color='secondary'
        size='sm'
        onClick={handleClick}
        isDisabled={isCreating}
        isLoading={isCreating}
      >
        Add to Mailchimp
      </Button>
    </div>
  );
}

export function MappingSections({
  mappings,
  availableAhoyProperties,
  savingId,
  creatingProp,
  onUpdate,
  onCreateMergeField,
}: MappingSectionsProps) {
  const synced = useMemo(() => mappings.filter(isSynced), [mappings]);
  const unmappedMcFields = useMemo(
    () => mappings.filter(m => !isSynced(m)),
    [mappings]
  );
  const unmappedAhoyProps = useMemo(() => {
    const mapped = new Set(
      mappings.map(m => m.ahoy_property).filter((n): n is string => !!n)
    );
    return availableAhoyProperties.filter(p => !mapped.has(p.name));
  }, [mappings, availableAhoyProperties]);

  return (
    <div className='space-y-8'>
      {synced.length > 0 && (
        <section className='space-y-2'>
          <h3 className={SECTION_HEADING_CLASS}>Synced fields</h3>
          <MappingTable
            mappings={synced}
            availableAhoyProperties={availableAhoyProperties}
            savingId={savingId}
            onUpdate={onUpdate}
          />
        </section>
      )}

      <section className='space-y-4'>
        <h3 className={SECTION_HEADING_CLASS}>Other fields</h3>

        {/* Subscriber status is a Mailchimp member attribute, not a merge field —
            it's synced automatically and isn't user-mappable. */}
        <div className='flex items-center justify-between gap-3 rounded-xl border border-[var(--color-border-secondary)] px-4 py-3'>
          <div className='flex items-center gap-2.5'>
            <RefreshCw01
              className='h-4 w-4 shrink-0 text-[var(--color-text-tertiary)]'
              aria-hidden='true'
            />
            <span className='text-sm font-medium text-[var(--color-text-primary)]'>
              Subscriber status
            </span>
            <span className='text-xs text-[var(--color-text-tertiary)]'>
              → Mailchimp status
            </span>
          </div>
          <span className='text-body-small text-[var(--color-text-tertiary)]'>
            Synced automatically
          </span>
        </div>

        {unmappedMcFields.length > 0 && (
          <div className='space-y-2'>
            <p className={CAPTION_CLASS}>
              Mailchimp fields you haven’t mapped yet — map to an existing Ahoy
              property or create one.
            </p>
            <MappingTable
              mappings={unmappedMcFields}
              availableAhoyProperties={availableAhoyProperties}
              savingId={savingId}
              onUpdate={onUpdate}
            />
          </div>
        )}

        {unmappedAhoyProps.length > 0 && (
          <div className='space-y-2'>
            <p className={CAPTION_CLASS}>
              Ahoy properties not in Mailchimp — add them as a Mailchimp merge
              field to start syncing them up.
            </p>
            <div className='divide-y divide-[var(--color-border-secondary)] overflow-hidden rounded-xl border border-[var(--color-border-secondary)]'>
              {unmappedAhoyProps.map(option => (
                <AhoyFieldRow
                  key={option.name}
                  option={option}
                  isCreating={creatingProp === option.name}
                  onCreate={onCreateMergeField}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
